<template>
    <new-student-backend-layout
        page-title="Student Dashboard"
        content-bg-color="gray-125"
        header-bg-color="white"
    >
        <template #page_header>
            <div class="flex flex-col gap-4 pt-4">
                <h1 class="text-[26px] font-semibold">Dashboard</h1>
                <div class="mb-4 w-full lg:hidden">
                    <div class="mb-8">
                        <div class="text-[16px] font-semibold">
                            Readiness Score
                        </div>
                        <div class="text-[14px]">
                            A comprehensive score to check whether you’re ready
                            to take the NREMT exam
                        </div>
                    </div>
                    <div class="h-[120px] w-full">
                        <new-modal title="Readiness Score" size="4xl">
                            <template #trigger>
                                <StudentReadinessScoreWidget
                                    ring-size-classes="h-32 w-32"
                                    :score-percent="
                                        $page.props.studentReadinessScore.score
                                    "
                                    :compact="false"
                                />
                            </template>
                            <template #content>
                                <StudentReadinessDetails
                                    :score-percent="
                                        $page.props.studentReadinessScore.score
                                    "
                                    :score-details="
                                        $page.props.studentReadinessScore
                                            .details
                                    "
                                />
                            </template>
                        </new-modal>
                    </div>
                </div>
                <div class="overflow-x-auto">
                    <div
                        class="flex h-32 w-[1px] flex-row gap-4 md:h-40 md:w-auto lg:h-48"
                    >
                        <div class="flex-grow">
                            <StudentDashboardWidget
                                title="Incorrect Answers"
                                :value="$page.props.studentMissedQuestionsCount"
                            >
                                <div class="mt-auto p-3">
                                    <Link
                                        v-if="
                                            $page.props
                                                .studentMissedQuestionsCount
                                        "
                                        :href="
                                            route('student.quiz.start', {
                                                quiz_type: 'missed',
                                            })
                                        "
                                        class="rounded bg-primary-525 px-3 py-2 text-[14px] text-white hover:cursor-pointer hover:bg-primary-625"
                                    >Take Quiz
                                    </Link>
                                    <span
                                        v-else
                                        class="rounded bg-gray-125 px-3 py-2 text-[14px] text-gray-425 hover:cursor-not-allowed"
                                    >
                                        Take Quiz
                                    </span>
                                </div>
                            </StudentDashboardWidget>
                        </div>
                        <div class="flex-grow">
                            <StudentDashboardWidget
                                title="Average Score"
                                :value="
                                    Math.round(
                                        $page.props.studentAverageQuizScore *
                                            100
                                    ) + '%'
                                "
                                reason="Last 500 Questions"
                            />
                        </div>
                        <div class="hidden w-48 flex-none lg:block">
                            <new-modal title="Readiness Score" size="4xl">
                                <template #trigger>
                                    <StudentReadinessScoreWidget
                                        ring-size-classes="h-48 w-48"
                                        :score-percent="
                                            $page.props.studentReadinessScore
                                                .score
                                        "
                                        :compact="true"
                                    />
                                </template>
                                <template #content>
                                    <StudentReadinessDetails
                                        :score-percent="
                                            $page.props.studentReadinessScore
                                                .score
                                        "
                                        :score-details="
                                            $page.props.studentReadinessScore
                                                .details
                                        "
                                    />
                                </template>
                            </new-modal>
                        </div>

                        <div class="flex-grow">
                            <StudentDashboardWidget
                                title="<span class='md:hidden'>Qs</span><span class='hidden md:inline'>Questions</span> Answered"
                                :value="
                                    $page.props.studentTotalQuestionsAnswered.toLocaleString()
                                "
                                reason="All Time"
                            />
                        </div>
                        <div class="flex-grow">
                            <StudentDashboardWidget
                                title="Study Time"
                                reason="All Time"
                                help-text="Study time is a measurement of your time actively interacting with EMTprep content (quizzes, videos, flashcards, etc)."
                            >
                                <div
                                    class="flex items-center gap-1 px-4 pb-4 tracking-tight"
                                >
                                    <div
                                        class="text-[20px] font-semibold md:text-[23px] lg:text-[34px]"
                                    >
                                        {{
                                            $page.props.studentTimeSpentStudying
                                                .hours
                                        }}
                                    </div>
                                    <div
                                        class="text-[12px] text-gray-600 md:text-[14px]"
                                    >
                                        hr
                                    </div>
                                    <div
                                        v-if="
                                            $page.props.studentTimeSpentStudying
                                                .hours < 100
                                        "
                                        class="text-[20px] font-semibold md:text-[23px] lg:text-[34px]"
                                    >
                                        {{
                                            $page.props.studentTimeSpentStudying
                                                .minutes
                                        }}
                                    </div>
                                    <div
                                        class="text-[12px] text-gray-600 md:text-[14px]"
                                    >
                                        min
                                    </div>
                                </div>
                            </StudentDashboardWidget>
                        </div>
                    </div>
                </div>
            </div>
            <div class="-mb-4 mt-8">
                <MobileTabSelector
                    :tab-options="{
                        'focus-areas': 'Focus Areas',
                        'quiz-modes': 'Quiz Modes',
                    }"
                    :current-tab="mobileTab"
                    v-on:tab-selected="setMobileTab"
                />
            </div>
        </template>

        <template #content>
            <div class="px-0 py-4 md:px-4 lg:px-10">
                <StudentGroupClassroomGoals
                    v-if="$page.props.classroomGoals"
                    :classroom-goals="$page.props.classroomGoals"
                />

                <div
                    :class="
                        'mb-4' +
                        ' ' +
                        (mobileTab === 'focus-areas' ? '' : 'hidden md:block')
                    "
                >
                    <StudentQuizzesByFocusAreaTable
                        :student-focus-areas-with-performance="
                            studentFocusAreasWithPerformance
                        "
                    />
                </div>

                <div
                    :class="
                        'mb-4' +
                        ' ' +
                        (mobileTab === 'quiz-modes' ? '' : ' hidden md:block')
                    "
                >
                    <StudentQuizzesAdditionalQuizTypesTable/>
                </div>
            </div>
            <div class="hidden border-b-[3px] border-b-primary-525"></div>
        </template>
    </new-student-backend-layout>
</template>

<script>
import NewStudentBackendLayout from "@/_Layouts/NewStudentBackendLayout.vue";
import StudentDashboardWidget from "@/Components/StudentDashboardWidget.vue";
import StudentReadinessScoreWidget from "@/Components/StudentReadinessScoreWidget.vue";
import StudentQuizzesByFocusAreaTable from "@/Components/StudentQuizzesByFocusAreaTable.vue";
import StudentQuizzesAdditionalQuizTypesTable from "@/Components/StudentQuizzesAdditionalQuizTypesTable.vue";
import NewModal from "@/Components/NewModal.vue";
import StudentReadinessDetails from "@/Pages/NewStudentBackend/StudentReadinessDetails.vue";
import MobileTabSelector from "@/Pages/NewStudentBackend/MobileTabSelector.vue";
import StudentGroupClassroomGoals from "@/Pages/NewStudentBackend/StudentGroupClassroomGoals.vue";

export default {
    name: "StudentDashboard",
    components: {
        StudentGroupClassroomGoals,
        MobileTabSelector,
        StudentReadinessDetails,
        NewModal,
        StudentQuizzesAdditionalQuizTypesTable,
        StudentQuizzesByFocusAreaTable,
        StudentReadinessScoreWidget,
        StudentDashboardWidget,
        NewStudentBackendLayout,
    },
    props: {
        studentFocusAreasWithPerformance: {
            type: Object,
            default: {},
        },
    },
    data() {
        return {
            mobileTab: "focus-areas",
        };
    },
    methods: {
        setMobileTab(tabKey) {
            this.mobileTab = tabKey;
        },
    },

};
</script>
